// Single source of truth for /tools/nonprofit-mortgage-help.

const SITE = "https://www.homeapproach.com";

export const PAGE_PATH = "/tools/nonprofit-mortgage-help";
export const PAGE_URL = `${SITE}${PAGE_PATH}`;
export const PAGE_TITLE = "Nonprofit Organizations That Help You Get Mortgage-Ready";
export const PAGE_DESCRIPTION =
  "Compare HUD-approved counseling, NeighborWorks, NACA, Habitat, NFCC, and Framework: what each nonprofit actually does, who qualifies, what it costs, and how to find local mortgage-readiness help near you.";

export const HERO_PROMISE =
  "Nonprofits do the unglamorous work that gets a mortgage approved: fixing credit, building savings, and putting a real plan on paper. Here's what each national organization does, who it fits, and how to find the local affiliate that will actually sit down with you.";

export const SECTIONS = [
  { id: "what-readiness-includes", title: "What mortgage readiness includes" },
  { id: "counseling-vs-education", title: "Counseling vs. homebuyer education" },
  { id: "national-options", title: "Compare national nonprofit options" },
  { id: "local-help", title: "Find local help near you" },
  { id: "choose", title: "How to choose the right nonprofit" },
  { id: "proof", title: "Does it actually work?" },
  { id: "qualifications", title: "Qualification and fine print" },
  { id: "faq", title: "Common questions" },
];

export const READINESS_ITEMS: { label: string; text: string }[] = [
  {
    label: "Budget and credit counseling",
    text: "A counselor pulls your full credit picture with you, names the specific accounts dragging the score down, and builds a month-by-month budget that leaves room for a mortgage payment instead of assuming one.",
  },
  {
    label: "Affordability analysis",
    text: "Not the lender's maximum — a payment you can carry with your real expenses, including the ones debt-to-income ratios ignore. You leave knowing a price range, not a pre-approval fantasy.",
  },
  {
    label: "A written action plan",
    text: "HUD-approved counseling produces a written plan: which debts to pay in what order, what to save monthly, what to stop doing, and the target date you can realistically apply.",
  },
  {
    label: "Follow-up sessions",
    text: "The plan is not a one-off handout. Approved agencies schedule check-ins and re-pull credit so you can see the score move and adjust before you apply.",
  },
  {
    label: "One-on-one counseling vs. a group workshop",
    text: "Counseling is a private session about your file. Education is a class about the process. Most assistance programs require the class certificate; the private session is what actually changes your numbers. Do both when you can.",
  },
];

export const COUNSELING_VS_EDUCATION = {
  heading: "Counseling vs. homebuyer education — they are not interchangeable",
  rows: [
    {
      label: "One-on-one counseling",
      what: "A private session reviewing your credit, income, debts, and savings, ending in a written action plan.",
      when: "Any time your credit, DTI, or savings are not where they need to be — including years before you buy.",
      cost: "Almost always free at a HUD-approved agency.",
      output: "Written action plan plus follow-up.",
    },
    {
      label: "Homebuyer education course",
      what: "A structured curriculum (online or in person) covering the buying process, loans, insurance, and closing.",
      when: "Once you are within roughly a year of buying, or whenever an assistance program requires it.",
      cost: "Free to about $125; fees must be disclosed up front and are often waived.",
      output: "A completion certificate, typically valid 12 months.",
    },
  ],
  note: "Most down payment assistance programs require the certificate from an approved provider — check which providers your specific program accepts before you pay for a course.",
};

export type NonprofitOrg = {
  id: string;
  name: string;
  type: string;
  serviceType: string;
  bestFor: string;
  qualification: string;
  cost: string;
  nextStep: string;
  href: string;
  fineprint: string;
};

export const ORGS: NonprofitOrg[] = [
  {
    id: "hud",
    name: "HUD-approved housing counseling agencies",
    type: "counseling_network",
    serviceType: "One-on-one counseling and education",
    bestFor: "Anyone who wants a free, unbiased read on where they actually stand",
    qualification: "No income limit and no credit minimum — counseling is open to everyone",
    cost: "Free counseling; education courses may carry a modest disclosed fee",
    nextStep: "Search HUD's agency locator by ZIP code",
    href: "https://www.hud.gov/i_want_to/talk_to_a_housing_counselor",
    fineprint:
      "Services vary by agency. Some specialize in foreclosure or rental help rather than purchase readiness — ask what they focus on before you book.",
  },
  {
    id: "neighborworks",
    name: "NeighborWorks America network",
    type: "counseling_network",
    serviceType: "Counseling, education, and local down payment programs",
    bestFor: "Buyers who want counseling and local assistance from the same organization",
    qualification: "Varies by affiliate; many programs carry area income limits",
    cost: "Free counseling; some courses charge a fee",
    nextStep: "Find your nearest NeighborWorks organization",
    href: "https://www.neighborworks.org/",
    fineprint:
      "Every affiliate is an independent nonprofit. Programs, income caps, and available funds differ from city to city, and local funds can run out mid-year.",
  },
  {
    id: "naca",
    name: "NACA (Neighborhood Assistance Corporation of America)",
    type: "lender_program",
    serviceType: "Counseling plus its own mortgage product",
    bestFor: "Buyers with thin savings who can commit to a longer, structured process",
    qualification: "Must be an owner-occupant, not own other property, and complete NACA's counseling track",
    cost: "No fees to participate; membership dues apply",
    nextStep: "Attend a NACA homebuyer workshop",
    href: "https://www.naca.com/",
    fineprint:
      "NACA's mortgage terms are program-specific: no down payment, no closing costs, no PMI, but only through NACA's process and participating lenders. The timeline is long and the requirements are strict.",
  },
  {
    id: "habitat",
    name: "Habitat for Humanity",
    type: "homeownership_program",
    serviceType: "Affordable homeownership, sweat equity, and education",
    bestFor: "Lower-income households willing to contribute labor hours",
    qualification: "Income bands set by each local affiliate, plus need, willingness to partner, and ability to pay",
    cost: "No cost to apply; sweat equity hours required",
    nextStep: "Contact your local Habitat affiliate",
    href: "https://www.habitat.org/housing-help",
    fineprint:
      "Rules vary sharply by local affiliate. Income limits, sweat equity hours, waiting lists, and whether an affiliate is even accepting applications are all set locally — national eligibility pages are only a starting point.",
  },
  {
    id: "nfcc",
    name: "NFCC (National Foundation for Credit Counseling)",
    type: "credit_counseling",
    serviceType: "Credit and debt counseling, debt management plans",
    bestFor: "Buyers blocked by credit card debt, collections, or a low score",
    qualification: "Open to anyone; debt management plans have their own terms",
    cost: "Free initial counseling; debt management plans may carry a small monthly fee",
    nextStep: "Connect with an NFCC member agency",
    href: "https://www.nfcc.org/",
    fineprint:
      "A debt management plan can close revolving accounts, which may move your score in the short term. Ask how the plan interacts with a mortgage application before enrolling.",
  },
  {
    id: "framework-lisc",
    name: "Framework and LISC",
    type: "education",
    serviceType: "Online homebuyer education and local community support",
    bestFor: "Buyers who need a certificate quickly or have no local agency nearby",
    qualification: "Open enrollment for online courses; LISC programs vary by market",
    cost: "Course fee typically under $100; some programs sponsor it",
    nextStep: "Check whether your assistance program accepts the course",
    href: "https://frameworkhomeownership.org/",
    fineprint:
      "Not every assistance program accepts every online course. Confirm your program's approved provider list before paying — a certificate from the wrong provider does not count.",
  },
];

export const COMPARE_COLUMNS = [
  { key: "serviceType", label: "Service type" },
  { key: "bestFor", label: "Best-fit borrower" },
  { key: "qualification", label: "Qualification signals" },
  { key: "cost", label: "Cost cues" },
] as const;

export type BarrierRow = { id: string; barrier: string; options: string; why: string };

export const BARRIER_MATRIX: BarrierRow[] = [
  {
    id: "low_credit",
    barrier: "Low credit score or high DTI",
    options: "NFCC, then a HUD-approved agency",
    why: "Credit counseling attacks the debt directly; HUD counseling turns the result into a mortgage timeline.",
  },
  {
    id: "low_savings",
    barrier: "Little or no down payment savings",
    options: "NACA, NeighborWorks, local DPA through a HUD agency",
    why: "These are the paths that either remove the down payment requirement or layer grant money on top of it.",
  },
  {
    id: "certificate",
    barrier: "A program requires an education certificate",
    options: "Framework, LISC, or your local HUD-approved agency's course",
    why: "You need a certificate from a provider your specific program accepts — verify the list first.",
  },
  {
    id: "income_caps",
    barrier: "Income sits below area limits",
    options: "Habitat for Humanity, NeighborWorks affiliates",
    why: "Both are built around income-capped programs and can pair homeownership with subsidized terms.",
  },
  {
    id: "local_counseling",
    barrier: "You want to sit down with a person locally",
    options: "HUD agency locator, NeighborWorks affiliate",
    why: "Both networks are physically local, and in-person sessions are free at approved agencies.",
  },
];

export const PROOF_POINTS = [
  {
    stat: "22 points",
    label: "Average credit score gain",
    text: "Habitat homeowners gained an average of 22 credit score points through the program.",
  },
  {
    stat: "56% less likely",
    label: "To lose a home",
    text: "Habitat homeowners were 56% less likely to lose their home than comparable households.",
  },
  {
    stat: "35M+ clients",
    label: "Over 70+ years",
    text: "The NFCC network cites more than 35 million clients served across more than 70 years of operation.",
  },
];

export const LOCAL_CTA = {
  heading: "Now find the local one",
  text: "National organizations are directories. The help happens at a local affiliate or agency — and which programs are open, funded, and accepting applications is a ZIP code question. Start with the counselor locator, then check what down payment money stacks in your county.",
  primary: { label: "Find a HUD-approved counselor near you", href: "/tools/find-housing-counselor" },
  secondary: { label: "See down payment help in your area", href: "/stack" },
};

export const FAQS = [
  {
    id: "counseling-vs-education",
    q: "What's the difference between housing counseling and homebuyer education?",
    a: "Counseling is a one-on-one session about your specific credit, income, debts, and savings that ends in a written action plan. Education is a structured course about the buying process that ends in a completion certificate. Assistance programs usually require the certificate, but counseling is what actually changes your numbers. Most buyers benefit from doing both.",
  },
  {
    id: "perfect-credit",
    q: "Do I need perfect credit to work with a nonprofit?",
    a: "No. Nonprofit counseling exists specifically for buyers who are not ready yet. HUD-approved agencies have no credit minimum and no income limit for counseling. Programs like NFCC are built around clients carrying debt and damaged credit. Coming in with a low score is the normal starting point, not a disqualifier.",
  },
  {
    id: "down-payment-help",
    q: "Can a nonprofit help with my down payment?",
    a: "Some can directly. NeighborWorks affiliates often administer local down payment assistance, NACA's mortgage requires no down payment at all, and Habitat replaces part of the cost with sweat equity. HUD-approved counselors do not hand out funds themselves, but they know which state, county, and city programs you qualify for and how to stack them.",
  },
  {
    id: "find-local-agency",
    q: "How do I find a local agency I can trust?",
    a: "Use HUD's official agency locator or the CFPB's tool — both draw on the same HUD-approved list, so an agency in neither is not HUD-approved. Confirm the counselor is HUD-certified and the agency is HUD-approved, and never pay for a referral, a loan modification application, or a promised outcome.",
  },
  {
    id: "eligibility",
    q: "Who qualifies for these programs?",
    a: "Counseling and education are open to essentially everyone. Program-based help is where limits appear: Habitat and many NeighborWorks programs use area income bands, NACA requires owner-occupancy and no other property ownership, and local assistance programs add their own purchase price and occupancy rules. Because affiliates set their own rules, always confirm eligibility with the local office rather than the national page.",
  },
];

export const DISCLAIMER =
  "Program details, income limits, fees, and availability are set by each local affiliate or agency and change frequently. This guide is educational and is not lending, legal, or tax advice — confirm every requirement directly with the organization before you apply. Home Approach is not affiliated with the organizations listed here.";

export const NONPROFIT_MORTGAGE_JSON_LD: Record<string, unknown> = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      name: PAGE_TITLE,
      description: PAGE_DESCRIPTION,
      url: PAGE_URL,
      inLanguage: "en-US",
      isPartOf: { "@type": "WebSite", name: "Home Approach", url: SITE },
      publisher: {
        "@type": "Organization",
        name: "Home Approach",
        url: SITE,
        logo: { "@type": "ImageObject", url: `${SITE}/og-image.png` },
      },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: "Tools", item: `${SITE}/tools` },
        { "@type": "ListItem", position: 3, name: PAGE_TITLE, item: PAGE_URL },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ],
};
